import React, { useEffect, useRef, useState } from "react";
import axios, { AxiosResponse } from "axios";
import { DataTableWithoutButtons } from "components/DataTable/DataTableWithoutButtons";
import { AfDataType } from "utils/TableColumnData";
import handleWheel from "utils/handleOnWheel";
import { ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { Img } from "components";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import { roles } from "utils/constant";
import { utils, write, writeFile } from "xlsx";
import { saveAs } from "file-saver";

const PaymentOutstanding: React.FC<any> = (props) => {
  const ADT = AfDataType;
  const tableRef = useRef(null);

  const [tableData, setTableData] = useState<any>([]);
  const [tableDetails, setTableDetails] = useState<any>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" | null }>({
    key: "status",
    direction: "asc",
  });

  const [startDate, setStartDate] = useState<any>(moment().startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(moment().endOf("day").format("YYYY-MM-DD"));
  const [query, setQuery] = useState<any>({
    fromDate: moment().startOf("day"),
    toDate: moment().endOf("day"),
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [fetchedData, setFetchedData] = useState([]);
  const [isDataAdded, setIsDataAdded] = useState(false);

  const [selectedAgent, setSelectedAgent] = useState({ id: undefined, name: "--- Select User ---" });
  const [allAgents, setAllAgents] = useState([]);

  useEffect(() => {
    const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
    if (Object.keys(userD).length) {
      setUserDetails(userD.state.user);
      if (userD.state.user.roleName == roles.SUPER_ADMIN) {
        mutateListAgents({ subbranch: undefined });
      } else if (userD.state.user.roleName == roles.SUBBRANCH_ADMIN || userD.state.user.roleName == roles.CUSTOMER_SERVICE) {
        mutateListAgents({ subbranch: userD.state.user?.subbranch });
      } else {
        mutateListAgents({ subbranch: undefined });
      }
    }
  }, []);
  const { mutate: mutateListAgents }: any = useMutation(
    (qry: any) => ListCommanWay("/admin/user/list", { subbranch: qry?.subbranch, isActive: true }, { paginate: 100000 }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          setAllAgents([{ id: undefined, name: "--- Select User ---" }, ...data.data.data]);
        }
      },
      onError: (data: any) => {},
    }
  );

  const handleSubmit = async () => {
    console.log("Ad");
    setIsDataAdded(!isDataAdded);
    setQuery({
      fromDate: startDate ? moment(startDate).startOf("day") : moment().startOf("day"),
      toDate: endDate ? moment(endDate).endOf("day") : moment().endOf("day"),
      userId: userDetails.roleName == "DELIVERY" ? userDetails.id : selectedAgent.id ? selectedAgent.id : undefined,
    });
  };

  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_outstanding_report"],
    () =>
      ListCommanWay("/admin/outstanding/report", query, {
        include: [],
        paginate: 10000,
        doSum: true,
      }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          console.log("data.data", data.data);
          let tdata = [];

          // Sort data with "No Invoice" at bottom by default
          const sortedData = sortData(data.data, "customer_name", "asc");
          // remove status = "No Invoice" entries
          const filteredData = sortedData.filter((item: any) => item.status !== "No Invoice");
          setTableData(filteredData);
          setTableDetails([]);
        } else {
          setTableData([]);
        }
      },
      onError: (data) => {
        setTableData([]);
      },
    }
  );

  useEffect(() => {
    if (query.userId) {
      refetch();
    }
  }, [query]);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});

  const sortData = (data: any[], key: string, direction: "asc" | "desc" | null) => {
    if (!direction) return data;

    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Special handling for status to put "No Invoice" at bottom
      if (key === "status") {
        if (aVal === "No Invoice") return 1;
        if (bVal === "No Invoice") return -1;
      }

      // Handle numeric columns
      if (["total_billed", "total_received_net", "total_tds_deducted", "total_effective_received", "outstanding_amount"].includes(key)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      // Handle date column
      if (key === "last_payment_date") {
        aVal = aVal ? moment(aVal).valueOf() : 0;
        bVal = bVal ? moment(bVal).valueOf() : 0;
      }

      // Handle string comparison
      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" | null = "asc";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (sortConfig.direction === "desc") {
        direction = null;
      }
    }

    setSortConfig({ key, direction });

    if (direction) {
      const sorted = sortData(tableData, key, direction);
      setTableData(sorted);
    } else {
      // Reset to original order with status sort
      const sorted = sortData(tableData, "status", "asc");
      setTableData(sorted);
      setSortConfig({ key: "status", direction: "asc" });
    }
  };

  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return " ⇅";
    }
    if (sortConfig.direction === "asc") {
      return " ↑";
    }
    if (sortConfig.direction === "desc") {
      return " ↓";
    }
    return " ⇅";
  };

  const calculateTotals = () => {
    return tableData.reduce(
      (acc: any, row: any) => {
        acc.total_billed += Number(row.total_billed || 0);
        acc.total_received_net += Number(row.total_received_net || 0);
        acc.total_tds_deducted += Number(row.total_tds_deducted || 0);
        acc.total_effective_received += Number(row.total_effective_received || 0);
        acc.outstanding_amount += Number(row.outstanding_amount || 0);
        return acc;
      },
      {
        total_billed: 0,
        total_received_net: 0,
        total_tds_deducted: 0,
        total_effective_received: 0,
        outstanding_amount: 0,
      }
    );
  };

  const totals = calculateTotals();

  const exportToExcel = () => {
    const table = tableRef.current;

    const tableClone = table.cloneNode(true);

    const tfoot = tableClone.getElementsByTagName("tfoot");
    if (tfoot.length > 0) {
      tfoot[0].remove();
    }

    const ws = utils.table_to_sheet(tableClone);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Sheet1");

    Object.keys(ws).forEach((key) => {
      if (key.startsWith("!")) return;

      let cell = ws[key];
      if (cell && cell.h && typeof cell.h === "string") {
        cell.h = null;
      }
      if (cell && cell.l) {
        delete cell.l;
      }
    });

    const wbout = write(wb, { bookType: "xlsx", type: "array" });

    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "Delivery Report.xlsx");
  };

  return (
    <>
      <div className="relative">
        <table className="w-full table- mb-4 text-sm" id="myTable" ref={tableRef}>
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="bg-purple-200 font-semibold  z-10">
              <td className="border border-slate-200 px-2 text-center" colSpan={3}>
                Total
              </td>
              <td className="border border-slate-200 px-2 text-right">{totals.total_billed.toLocaleString("en-IN")}</td>
              <td className="border border-slate-200 px-2 text-right">{totals.total_received_net.toLocaleString("en-IN")}</td>
              <td className="border border-slate-200 px-2 text-right">{totals.total_tds_deducted.toLocaleString("en-IN")}</td>
              <td className="border border-slate-200 px-2 text-right">{totals.total_effective_received.toLocaleString("en-IN")}</td>
              <td className="border border-slate-200 px-2 text-right">{totals.outstanding_amount.toLocaleString("en-IN")}</td>
              <td className="border border-slate-200 px-2 text-center" colSpan={3}></td>
            </tr>
            <tr>
              <th className="border border-slate-200">Sr.</th>
              <th className="border border-slate-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort("customer_code")}>
                Customer code{getSortIcon("customer_code")}
              </th>
              <th className="border border-slate-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort("customer_name")}>
                Customer name{getSortIcon("customer_name")}
              </th>
              <th className="border border-slate-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort("total_billed")}>
                Total Invoice{getSortIcon("total_billed")}
              </th>
              <th className="border border-slate-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort("total_received_net")}>
                Total Received{getSortIcon("total_received_net")}
              </th>
              <th className="border border-slate-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort("total_tds_deducted")}>
                TDS Deducted{getSortIcon("total_tds_deducted")}
              </th>
              <th
                className="border border-slate-200 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("total_effective_received")}
              >
                Effective Received{getSortIcon("total_effective_received")}
              </th>
              <th className="border border-slate-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort("outstanding_amount")}>
                Outstanding Amt{getSortIcon("outstanding_amount")}
              </th>
              <th className="border border-slate-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort("last_payment_date")}>
                Last Pay Date{getSortIcon("last_payment_date")}
              </th>
              <th className="border border-slate-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort("gst_no")}>
                Gst No{getSortIcon("gst_no")}
              </th>
              <th className="border border-slate-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort("status")}>
                Status{getSortIcon("status")}
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((r, i) => {
              return (
                <tr key={"outstand-" + i} className={`${r["status"] === "Cleared" ? "bg-green-300" : ""}`}>
                  <td className="border border-slate-200 px-4 text-center">{i + 1}</td>
                  <td className="border border-slate-200 px-2 text-center">{r.customer_code}</td>
                  <td className="border border-slate-200 px-2 text-center font-RobotoCondensed">{r.customer_name}</td>
                  <td className="border border-slate-200 px-2 text-right">{Number(r.total_billed || 0)?.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 text-right">{Number(r.total_received_net || 0)?.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 text-right">{Number(r.total_tds_deducted || 0)?.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 text-right">
                    {Number(r.total_effective_received || 0)?.toLocaleString("en-IN")}
                  </td>
                  <td className="border border-slate-200 px-2 text-right">{Number(r.outstanding_amount || 0)?.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 text-center whitespace-nowrap">
                    {r.last_payment_date ? moment(r.last_payment_date).format("DD-MM-YYYY") : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-center">{r.gst_no}</td>
                  <td className="border border-slate-200 px-2 text-center">{r.status}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-purple-200 font-semibold">
              <td className="border border-slate-200 px-2 text-center" colSpan={3}>
                Total
              </td>
              <td className="border border-slate-200 px-2 text-right">{totals.total_billed.toLocaleString("en-IN")}</td>
              <td className="border border-slate-200 px-2 text-right">{totals.total_received_net.toLocaleString("en-IN")}</td>
              <td className="border border-slate-200 px-2 text-right">{totals.total_tds_deducted.toLocaleString("en-IN")}</td>
              <td className="border border-slate-200 px-2 text-right">{totals.total_effective_received.toLocaleString("en-IN")}</td>
              <td className="border border-slate-200 px-2 text-right">{totals.outstanding_amount.toLocaleString("en-IN")}</td>
              <td className="border border-slate-200 px-2 text-center" colSpan={3}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

export default PaymentOutstanding;
